import React, { useState, useEffect, useCallback } from 'react';
import './WatchlistBrief.css';

const SLOTS = [
  { key: 'morning', label: 'Morning', time: '6:30 AM' },
  { key: 'midday', label: 'Midday', time: '10:00 AM' },
  { key: 'powerhour', label: 'Power Hour', time: '12:30 PM' },
];

const TIERS = ['Mega', 'Large', 'Mid', 'Small'];
const SECTORS = ['', 'space', 'chips', 'ai-infra', 'fuel-cell', 'china', 'crypto', 'biotech', 'energy', 'fintech', 'auto'];

function fmtTime(iso) {
  if (!iso) return '—';
  try {
    return new Date(iso).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
  } catch {
    return iso;
  }
}

function fmtRelative(iso) {
  if (!iso) return '';
  const diff = Date.now() - new Date(iso).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ${mins % 60}m ago`;
  return `${Math.floor(hrs / 24)}d ago`;
}

// Render markdown bullets/contracts inline
function renderInline(text) {
  if (!text) return null;
  // Highlight `INLINE CODE` (option contracts)
  const parts = text.split(/(`[^`]+`)/g);
  return parts.map((p, i) => {
    if (p.startsWith('`') && p.endsWith('`')) {
      return <code key={i} className="wb-contract">{p.slice(1, -1)}</code>;
    }
    // Bold **text**
    const bolded = p.split(/(\*\*[^*]+\*\*)/g).map((b, j) => {
      if (b.startsWith('**') && b.endsWith('**')) {
        return <strong key={j}>{b.slice(2, -2)}</strong>;
      }
      return b;
    });
    return <React.Fragment key={i}>{bolded}</React.Fragment>;
  });
}

// Parse a setup bullet into structured fields.
// Format examples:
//   - **INTC** [Mega]: P/C 0.16, 4.74x vol, 150C 27k flow. Play: `INTC 140 C 6/18` at ~$4.28 mid (~$430 premium, d=0.30)
//   - **LONG INTC** [Mega]: Above $116 with vol confirm. Contract: `INTC 120 C 5/22` weekly. Stop: $114. Target: $120
//   - **INTC** [Mega]: `INTC 140 C 6/18` at ~$4.28 mid. Entry: now. Stop: INTC <$112. Target: $130 / 50% gain
function parseSetup(rawLine) {
  const line = rawLine.replace(/^[-*]\s*/, '').trim();

  // **DIRECTION TICKER** or **TICKER**
  const headerMatch = line.match(/\*\*\s*([^*]+?)\s*\*\*/);
  const header = headerMatch?.[1] || '';
  let direction = '';
  let ticker = header;
  const dirMatch = header.match(/^(LONG|SHORT)\s+(.+)$/i);
  if (dirMatch) {
    direction = dirMatch[1].toUpperCase();
    ticker = dirMatch[2];
  }

  const sizeMatch = line.match(/\[(Mega|Large|Mid|Small)\]/i);
  const contractMatch = line.match(/`([^`]+)`/);
  const midMatch = line.match(/~?\$([\d.]+)\s*mid/);
  const premiumMatch = line.match(/~?\$(\d+(?:\.\d+)?)\s*K?\s*premium/i);
  const deltaMatch = line.match(/(?:d(?:elta)?|Δ)\s*=?\s*([+-]?[\d.]+)/i);

  // Entry / Stop / Target labels (for day-trade and swing entries)
  const entryMatch = line.match(/Entry:\s*([^.\n]+?)(?:\.|Stop:|Target:|$)/i);
  const stopMatch = line.match(/Stop:\s*([^.\n]+?)(?:\.|Target:|$)/i);
  const targetMatch = line.match(/Target:\s*([^.\n]+?)(?:\.|Entry:|Stop:|$)/i);

  // Reasoning: text between "]: " and "Play:" / "Contract:" / first contract backtick.
  let reasoning = '';
  const afterHead = line.replace(/^\*\*[^*]+\*\*\s*(?:\[[^\]]+\])?\s*:?\s*/, '');
  const cutAt = afterHead.search(/\s+(?:Play:|Contract:|`)/i);
  if (cutAt > 0) {
    reasoning = afterHead.slice(0, cutAt).trim().replace(/[.,]$/, '');
  } else if (!contractMatch) {
    // No play extracted — show full text as reasoning
    reasoning = afterHead.trim();
  }

  return {
    ticker,
    direction,
    size: sizeMatch?.[1] || '',
    contract: contractMatch?.[1] || '',
    mid: midMatch?.[1] || '',
    premium: premiumMatch?.[1] || '',
    delta: deltaMatch?.[1] || '',
    entry: entryMatch?.[1]?.trim() || '',
    stop: stopMatch?.[1]?.trim() || '',
    target: targetMatch?.[1]?.trim() || '',
    reasoning,
    raw: line,
  };
}

// Render trend + realized-vol qualifier badges (weekly-actionable.md #65)
function QualBadges({ qual }) {
  if (!qual || (qual.trend_60d_pct == null && qual.realized_vol_20d_ann == null)) return null;
  const trendPct = qual.trend_60d_pct != null ? (qual.trend_60d_pct * 100).toFixed(0) : '—';
  const rvPct = qual.realized_vol_20d_ann != null ? (qual.realized_vol_20d_ann * 100).toFixed(0) : '—';
  return (
    <>
      {qual.qualified && (
        <span className="wb-badge wb-badge-qualified" title={`Trend +${trendPct}% / RV ${rvPct}% — both gates pass`}>
          ★ qualified
        </span>
      )}
      {qual.trend_pass && !qual.qualified && (
        <span className="wb-badge wb-badge-trend" title={`60-day return +${trendPct}% (gate: ≥+25%)`}>
          ↑{trendPct}%/60d
        </span>
      )}
      {qual.rv_pass && !qual.qualified && (
        <span className="wb-badge wb-badge-rv" title={`20-day annualized realized vol ${rvPct}% (gate: ≥40%)`}>
          RV{rvPct}
        </span>
      )}
    </>
  );
}

function SetupCard({ setup, variant, qualifiers }) {
  const hasPlay = !!setup.contract;
  const qual = setup.ticker && qualifiers ? qualifiers[setup.ticker] : null;
  if (!hasPlay) {
    // No structured play — render the raw line text-only
    return (
      <div className={`wb-setup wb-setup-${variant} wb-setup-text`}>
        {renderInline(setup.raw)}
        {qual && (
          <span className="wb-setup-quals-inline">
            <QualBadges qual={qual} />
          </span>
        )}
      </div>
    );
  }
  return (
    <div className={`wb-setup wb-setup-${variant}`}>
      <div className="wb-setup-play">
        {setup.direction && (
          <span className={`wb-direction wb-direction-${setup.direction.toLowerCase()}`}>
            {setup.direction}
          </span>
        )}
        <code className="wb-contract wb-contract-lg">{setup.contract}</code>
        {setup.mid && <span className="wb-setup-price">${setup.mid} mid</span>}
      </div>
      <div className="wb-setup-meta">
        {setup.ticker && <span className="wb-setup-ticker">{setup.ticker}</span>}
        {setup.size && <span className="wb-setup-size">{setup.size}</span>}
        {setup.delta && <span className="wb-setup-delta">Δ {setup.delta}</span>}
        {setup.premium && <span className="wb-setup-premium">~${setup.premium} premium</span>}
        <QualBadges qual={qual} />
      </div>
      {(setup.entry || setup.stop || setup.target) && (
        <div className="wb-setup-trade">
          {setup.entry && <span><b>Entry:</b> {setup.entry}</span>}
          {setup.stop && <span className="wb-trade-stop"><b>Stop:</b> {setup.stop}</span>}
          {setup.target && <span className="wb-trade-target"><b>Target:</b> {setup.target}</span>}
        </div>
      )}
      {setup.reasoning && (
        <div className="wb-setup-reasoning">{renderInline(setup.reasoning)}</div>
      )}
    </div>
  );
}

function SetupList({ text, variant, emptyText = 'No setups', qualifiers }) {
  if (!text || !text.trim()) {
    return <div className="wb-empty">{emptyText}</div>;
  }
  const bullets = text
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.startsWith('-') || l.startsWith('*'));
  if (bullets.length === 0) {
    return <p className="wb-paragraph">{renderInline(text)}</p>;
  }
  return (
    <div className="wb-setups">
      {bullets.map((b, i) => (
        <SetupCard key={i} setup={parseSetup(b)} variant={variant} qualifiers={qualifiers} />
      ))}
    </div>
  );
}

// Render a bullet list section
function BulletList({ text, emptyText = 'No items' }) {
  if (!text || !text.trim()) {
    return <div className="wb-empty">{emptyText}</div>;
  }
  // Split into bullets — each line starting with - or *
  const bullets = text
    .split('\n')
    .map(l => l.trim())
    .filter(l => l.startsWith('-') || l.startsWith('*'))
    .map(l => l.replace(/^[-*]\s*/, ''));
  if (bullets.length === 0) {
    // Just render as paragraph
    return <p className="wb-paragraph">{renderInline(text)}</p>;
  }
  return (
    <ul className="wb-bullets">
      {bullets.map((b, i) => (
        <li key={i}>{renderInline(b)}</li>
      ))}
    </ul>
  );
}

// Identify ticker color from row data
function pctClass(s) {
  if (!s) return '';
  const m = String(s).match(/([+-]?[\d.]+)\s*%?/);
  if (!m) return '';
  const v = parseFloat(m[1]);
  if (isNaN(v)) return '';
  if (v > 0) return 'wb-pos';
  if (v < 0) return 'wb-neg';
  return '';
}

function readClass(s) {
  if (!s) return '';
  if (s.includes('🟢')) return 'wb-read-bull';
  if (s.includes('🔴')) return 'wb-read-bear';
  if (s.includes('🟡')) return 'wb-read-mixed';
  return '';
}

function TickerTable({ data, title }) {
  if (!data || !data.rows || data.rows.length === 0) {
    return null;
  }
  return (
    <div className="wb-card">
      <h3 className="wb-card-title">{title}</h3>
      <div className="wb-table-wrap">
        <table className="wb-table">
          <thead>
            <tr>
              {data.headers.map((h, i) => (
                <th key={i}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {data.rows.map((row, i) => (
              <tr key={i}>
                {data.headers.map((h, j) => {
                  const cell = row[h] || '';
                  let cls = '';
                  if (h.includes('%') || h.toLowerCase().includes('chg')) cls = pctClass(cell);
                  if (h.toLowerCase().includes('read')) cls = readClass(cell);
                  return (
                    <td key={j} className={cls}>
                      {renderInline(cell)}
                    </td>
                  );
                })}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function SlotPill({ slot, brief, active, onClick }) {
  const hasData = !!brief;
  const status = hasData ? '✓' : '○';
  const time = hasData ? fmtTime(brief.mtime) : slot.time;
  return (
    <button
      className={`wb-pill ${active ? 'wb-pill-active' : ''} ${hasData ? 'wb-pill-done' : 'wb-pill-pending'}`}
      onClick={onClick}
    >
      <span className="wb-pill-status">{status}</span>
      <span className="wb-pill-label">{slot.label}</span>
      <span className="wb-pill-time">{time}</span>
    </button>
  );
}

function BriefContent({ brief, slotLabel, qualifiers }) {
  if (!brief) {
    return (
      <div className="wb-empty-state">
        <div className="wb-empty-icon">⏱</div>
        <div className="wb-empty-text">
          {slotLabel} brief hasn't run yet today.
        </div>
      </div>
    );
  }
  return (
    <>
      {brief.overall && (
        <div className="wb-card wb-card-overall">
          <h3 className="wb-card-title">Tape Overall</h3>
          <p className="wb-overall-text">{renderInline(brief.overall)}</p>
        </div>
      )}

      {brief.sectorRead && (
        <div className="wb-card wb-card-sector">
          <h3 className="wb-card-title">Sector Heat</h3>
          <BulletList text={brief.sectorRead} />
        </div>
      )}

      {brief.unusualFlow && (
        <div className="wb-card wb-card-unusual">
          <h3 className="wb-card-title">🚨 Unusual Flow</h3>
          <BulletList text={brief.unusualFlow} emptyText="No flagged unusual flow" />
        </div>
      )}

      <div className="wb-grid-2">
        {brief.bullSetups && (
          <div className="wb-card wb-card-bull">
            <h3 className="wb-card-title">💰 Bull Setups</h3>
            <SetupList text={brief.bullSetups} variant="bull" emptyText="No bull setups" qualifiers={qualifiers} />
          </div>
        )}
        {brief.bearSetups && (
          <div className="wb-card wb-card-bear">
            <h3 className="wb-card-title">🩸 Bear Setups</h3>
            <SetupList text={brief.bearSetups} variant="bear" emptyText="No bear setups" qualifiers={qualifiers} />
          </div>
        )}
      </div>

      {brief.beyondWatchlist && (
        <TickerTable data={brief.beyondWatchlist} title="🌐 Beyond Watchlist (Top Unusual Movers)" />
      )}

      {brief.perTicker && (
        <TickerTable data={brief.perTicker} title="Per-Ticker Snapshot" />
      )}

      <div className="wb-grid-2">
        {brief.dayTradeEntries && (
          <div className="wb-card">
            <h3 className="wb-card-title">⚡ Day-Trade Entries</h3>
            <SetupList text={brief.dayTradeEntries} variant="day" emptyText="No day-trade entries" />
          </div>
        )}
        {brief.swingEntries && (
          <div className="wb-card">
            <h3 className="wb-card-title">📈 Swing Entries</h3>
            <SetupList text={brief.swingEntries} variant="swing" emptyText="No swing entries" />
          </div>
        )}
      </div>

      {brief.reversals && (
        <div className="wb-card">
          <h3 className="wb-card-title">🔄 Reversals (Open → Close)</h3>
          <BulletList text={brief.reversals} />
        </div>
      )}

      {brief.overnightBias && (
        <div className="wb-card">
          <h3 className="wb-card-title">🌙 Overnight Bias</h3>
          <p className="wb-paragraph">{renderInline(brief.overnightBias)}</p>
        </div>
      )}

      {brief.tradeAction && (
        <div className="wb-card wb-card-action">
          <h3 className="wb-card-title">Trade Action</h3>
          <BulletList text={brief.tradeAction} />
        </div>
      )}

      {brief.skipList && (
        <div className="wb-card wb-card-skip">
          <h3 className="wb-card-title">⏭ Skip List</h3>
          <BulletList text={brief.skipList} />
        </div>
      )}
    </>
  );
}

function WatchlistManager() {
  const [byTier, setByTier] = useState({ Mega: [], Large: [], Mid: [], Small: [] });
  const [expanded, setExpanded] = useState(false);
  const [newTicker, setNewTicker] = useState('');
  const [newTier, setNewTier] = useState('Mid');
  const [newSector, setNewSector] = useState('');
  const [error, setError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [profilePreview, setProfilePreview] = useState(null);
  const [profileLoading, setProfileLoading] = useState(false);
  const [autoFilled, setAutoFilled] = useState(false);

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/brief-watchlist');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setByTier(j.byTier || { Mega: [], Large: [], Mid: [], Small: [] });
      setError(null);
    } catch (e) {
      setError(e.message);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const autoFill = useCallback(async (ticker) => {
    const t = ticker.trim().toUpperCase();
    if (!t || !/^[A-Z0-9.\-]{1,8}$/.test(t)) return;
    setProfileLoading(true);
    setError(null);
    try {
      const r = await fetch(`/api/brief-watchlist/profile/${t}`);
      const j = await r.json();
      if (!r.ok || j.error) {
        setProfilePreview(null);
        // Don't surface 404 as error — user can still add manually
        if (r.status !== 404) setError(j.error || `HTTP ${r.status}`);
        return;
      }
      setProfilePreview(j);
      // Only auto-fill if user hasn't manually changed the defaults
      if (newTier === 'Mid') setNewTier(j.size_tier || 'Mid');
      if (!newSector && j.sector) setNewSector(j.sector);
      setAutoFilled(true);
    } catch (e) {
      setError(e.message);
    } finally {
      setProfileLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const add = async () => {
    const t = newTicker.trim().toUpperCase();
    if (!t) return;
    setBusy(true);
    try {
      // If user clicked add without blur-triggered autofill, fetch now (synchronous-feel)
      if (!profilePreview && !profileLoading) {
        await autoFill(t);
      }
      const r = await fetch('/api/brief-watchlist', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ ticker: t, size_tier: newTier, sector: newSector || null }),
      });
      if (!r.ok) {
        const j = await r.json();
        throw new Error(j.error || `HTTP ${r.status}`);
      }
      setNewTicker('');
      setNewSector('');
      setNewTier('Mid');
      setProfilePreview(null);
      setAutoFilled(false);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const remove = async (ticker) => {
    if (!window.confirm(`Remove ${ticker} from the watchlist?`)) return;
    setBusy(true);
    try {
      const r = await fetch(`/api/brief-watchlist/${ticker}`, { method: 'DELETE' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      await load();
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  };

  const total = Object.values(byTier).reduce((s, v) => s + v.length, 0);

  return (
    <div className="wb-card wb-manage">
      <div className="wb-manage-header" onClick={() => setExpanded(!expanded)}>
        <h3 className="wb-card-title">
          ⚙ Manage Watchlist <span className="wb-manage-count">({total} tickers)</span>
        </h3>
        <span className="wb-manage-toggle">{expanded ? '▲' : '▼'}</span>
      </div>

      {expanded && (
        <div className="wb-manage-body">
          {TIERS.map(tier => (
            <div key={tier} className="wb-manage-tier">
              <span className={`wb-tier-label wb-tier-${tier.toLowerCase()}`}>{tier}</span>
              <div className="wb-tier-tickers">
                {byTier[tier]?.length === 0 && <span className="wb-tier-empty">—</span>}
                {byTier[tier]?.map(t => (
                  <span key={t.ticker} className="wb-ticker-chip">
                    {t.ticker}
                    {t.sector && <span className="wb-ticker-sector">·{t.sector}</span>}
                    <button
                      className="wb-ticker-remove"
                      onClick={() => remove(t.ticker)}
                      disabled={busy}
                      title={`Remove ${t.ticker}`}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
            </div>
          ))}

          <div className="wb-manage-add">
            <div className="wb-input-wrap">
              <input
                type="text"
                placeholder="Ticker (e.g. NVDA)"
                value={newTicker}
                onChange={(e) => {
                  setNewTicker(e.target.value.toUpperCase());
                  setProfilePreview(null);
                  setAutoFilled(false);
                }}
                onBlur={(e) => {
                  const t = e.target.value.trim().toUpperCase();
                  if (t && !profilePreview) autoFill(t);
                }}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    if (!profilePreview && !profileLoading) {
                      autoFill(e.target.value);
                    } else {
                      add();
                    }
                  }
                }}
                maxLength={8}
                className="wb-manage-input"
              />
              {profileLoading && <span className="wb-input-spinner">⟳</span>}
              {autoFilled && !profileLoading && <span className="wb-input-check" title="Auto-filled from market data">✓</span>}
            </div>
            <select
              value={newTier}
              onChange={(e) => { setNewTier(e.target.value); setAutoFilled(false); }}
              className={`wb-manage-select ${autoFilled ? 'wb-autofilled' : ''}`}
            >
              {TIERS.map(t => <option key={t} value={t}>{t}</option>)}
            </select>
            <select
              value={newSector}
              onChange={(e) => { setNewSector(e.target.value); setAutoFilled(false); }}
              className={`wb-manage-select ${autoFilled ? 'wb-autofilled' : ''}`}
            >
              {SECTORS.map(s => <option key={s} value={s}>{s || '(no sector)'}</option>)}
            </select>
            <button onClick={add} disabled={busy || !newTicker} className="wb-manage-add-btn">
              {busy ? '…' : '+ Add'}
            </button>
          </div>

          {profilePreview && (
            <div className="wb-profile-preview">
              <strong>{profilePreview.company_name}</strong>
              {profilePreview.market_cap && (
                <span> · ${(profilePreview.market_cap / 1e9).toFixed(1)}B mkt cap</span>
              )}
              {profilePreview.industry_raw && (
                <span className="wb-profile-industry"> · {profilePreview.industry_raw}</span>
              )}
            </div>
          )}

          {error && <div className="wb-manage-error">⚠ {error}</div>}
          <div className="wb-manage-hint">
            Type a ticker and tab/Enter to auto-fill cap tier + sector. Override before clicking Add. Changes apply on the next scheduled brief.
          </div>
        </div>
      )}
    </div>
  );
}

// Snapshot section: on-demand per-user data dump. Useful for accounts that don't
// have their own scheduled-brief setup (i.e., everyone except Paul). Shows live
// Tradier prices + trend/RV qualifiers for the current user's brief watchlist.
function SnapshotSection() {
  const [snapshot, setSnapshot] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  // Load any persisted snapshot from today on mount so a page refresh doesn't lose state.
  useEffect(() => {
    fetch('/api/watchlist-brief/snapshot/latest', { credentials: 'include' })
      .then(r => r.json())
      .then(j => {
        if (j?.snapshot) {
          // Re-fetch a fresh JSON version on first generate; for the cached read we just
          // surface a "last generated at" timestamp so the user knows whether to refresh.
          setSnapshot({ generated_at: j.snapshot.generated_at, rows: null, cached: true });
        }
      })
      .catch(() => { /* non-fatal */ });
  }, []);

  async function generate() {
    setBusy(true);
    setError(null);
    try {
      const r = await fetch('/api/watchlist-brief/snapshot', { method: 'POST', credentials: 'include' });
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setSnapshot(j);
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="wb-snapshot">
      <div className="wb-snapshot-head">
        <div>
          <h3 className="wb-snapshot-title">📊 Live Snapshot</h3>
          <div className="wb-snapshot-sub">
            {snapshot
              ? `Last generated ${fmtRelative(snapshot.generated_at)}${snapshot.cached ? ' (cached — refresh for fresh data)' : ''}`
              : 'On-demand fresh quotes + trend/RV for your watchlist'}
          </div>
        </div>
        <button className="wb-refresh" onClick={generate} disabled={busy}>
          {busy ? 'Generating…' : snapshot ? '↻ Refresh' : '⚡ Generate'}
        </button>
      </div>
      {error && <div className="wb-error">Snapshot failed: {error}</div>}
      {snapshot?.note && <div className="wb-snapshot-note">{snapshot.note}</div>}
      {snapshot?.rows && snapshot.rows.length > 0 && (
        <table className="wb-snapshot-table">
          <thead>
            <tr>
              <th>Ticker</th><th>Tier</th><th>Sector</th>
              <th style={{ textAlign: 'right' }}>Price</th>
              <th style={{ textAlign: 'right' }}>%Chg</th>
              <th style={{ textAlign: 'right' }}>Volume</th>
              <th style={{ textAlign: 'right' }}>60d Trend</th>
              <th style={{ textAlign: 'right' }}>20d RV</th>
              <th>Flags</th>
            </tr>
          </thead>
          <tbody>
            {snapshot.rows.map(r => {
              const chgClass = r.change_pct == null ? '' : r.change_pct >= 0 ? 'wb-pos' : 'wb-neg';
              return (
                <tr key={r.ticker}>
                  <td><strong>{r.ticker}</strong></td>
                  <td>{r.size_tier}</td>
                  <td>{r.sector || '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.price != null ? `$${r.price.toFixed(2)}` : '—'}</td>
                  <td style={{ textAlign: 'right' }} className={chgClass}>
                    {r.change_pct != null ? `${r.change_pct >= 0 ? '+' : ''}${r.change_pct.toFixed(2)}%` : '—'}
                  </td>
                  <td style={{ textAlign: 'right' }}>{r.volume != null ? r.volume.toLocaleString() : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.trend_60d_pct != null ? `${(r.trend_60d_pct * 100).toFixed(1)}%` : '—'}</td>
                  <td style={{ textAlign: 'right' }}>{r.realized_vol_20d_ann != null ? `${(r.realized_vol_20d_ann * 100).toFixed(0)}%` : '—'}</td>
                  <td>
                    {r.qualified && <span className="wb-flag wb-flag-q" title="Trend ≥+25%/60d AND RV ≥40%">★Q</span>}
                    {!r.qualified && r.trend_pass && <span className="wb-flag wb-flag-t" title="60d trend ≥+25%">↑trend</span>}
                    {!r.qualified && r.rv_pass && <span className="wb-flag wb-flag-rv" title="20d RV ≥40%">RV</span>}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </div>
  );
}

function WatchlistBrief() {
  const [data, setData] = useState(null);
  const [activeSlot, setActiveSlot] = useState('morning');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [qualifiers, setQualifiers] = useState({});

  const load = useCallback(async () => {
    try {
      const r = await fetch('/api/watchlist-brief/today');
      if (!r.ok) throw new Error(`HTTP ${r.status}`);
      const j = await r.json();
      setData(j);
      // Auto-select the most recent brief
      const latestSlot = ['powerhour', 'midday', 'morning'].find(s => j[s]) || 'morning';
      setActiveSlot(latestSlot);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, []);

  const loadQualifiers = useCallback(async () => {
    try {
      const r = await fetch('/api/brief-watchlist/qualifiers');
      if (!r.ok) return;  // silent — non-fatal feature
      setQualifiers(await r.json());
    } catch (e) {
      // non-fatal — badges just won't render
    }
  }, []);

  useEffect(() => {
    load();
    const interval = setInterval(load, 60000);
    return () => clearInterval(interval);
  }, [load]);

  useEffect(() => {
    loadQualifiers();
    // Qualifiers are daily-bar metrics — refresh once per hour
    const interval = setInterval(loadQualifiers, 60 * 60 * 1000);
    return () => clearInterval(interval);
  }, [loadQualifiers]);

  const activeBrief = data?.[activeSlot];
  const qualifiedCount = Object.values(qualifiers).filter(q => q?.qualified).length;
  const totalQualCount = Object.values(qualifiers).filter(q => q && q.trend_60d_pct != null).length;

  return (
    <div className="wb-page">
      <div className="wb-header">
        <div>
          <h2 className="wb-title">Watchlist Brief</h2>
          <div className="wb-subtitle">
            {data?.date || '—'}
            {activeBrief?.mtime && (
              <span className="wb-stale">· updated {fmtRelative(activeBrief.mtime)}</span>
            )}
          </div>
        </div>
        <button className="wb-refresh" onClick={load} disabled={loading}>
          ↻ Refresh
        </button>
      </div>

      <div className="wb-pills">
        {SLOTS.map(slot => (
          <SlotPill
            key={slot.key}
            slot={slot}
            brief={data?.[slot.key]}
            active={activeSlot === slot.key}
            onClick={() => setActiveSlot(slot.key)}
          />
        ))}
        {totalQualCount > 0 && (
          <span
            className="wb-qual-rollup"
            title="Universe tickers with trend ≥+25%/60d AND realized vol ≥40% (#65)"
          >
            ★ Qualified: {qualifiedCount}/{totalQualCount}
          </span>
        )}
      </div>

      {activeBrief?.workflow && activeBrief.workflow !== 'OK' && activeBrief.workflow !== 'unknown' && (
        <div className="wb-workflow-warn">⚠ Workflow: {activeBrief.workflow}</div>
      )}

      {error && <div className="wb-error">Error loading: {error}</div>}

      <WatchlistManager />

      {/* SnapshotSection — disabled per user request 2026-05-27, kept in file for later re-enable */}
      {/* <SnapshotSection /> */}

      {loading && !data ? (
        <div className="wb-loading">Loading…</div>
      ) : (
        <div className="wb-content">
          <BriefContent brief={activeBrief} slotLabel={SLOTS.find(s => s.key === activeSlot)?.label} qualifiers={qualifiers} />
        </div>
      )}
    </div>
  );
}

export default WatchlistBrief;
